/**
 * Token 管理工具
 *
 * 提供 Token 過期檢查和自動刷新功能：
 * - Token 過期時間追蹤
 * - 自動刷新機制
 * - Session 安全管理
 */

import { SECURITY_CONSTANTS } from './security';
import { createLogger } from './logger';

const logger = createLogger('TokenManager');

// ========================================================================
// Token Manager 類別
// ========================================================================

export class TokenManager {
  /**
   * 檢查 Token 是否過期
   * @returns true 表示已過期或無 Token
   */
  static isTokenExpired(): boolean {
    const expiryStr = sessionStorage.getItem('token_expiry');
    if (!expiryStr) {
      logger.warn('No token expiry found');
      return true;
    }

    const expiry = parseInt(expiryStr, 10);
    if (isNaN(expiry)) {
      logger.error('Invalid token expiry format');
      return true;
    }

    const now = Date.now();
    const isExpired = now > expiry;

    if (isExpired) {
      logger.info('Token has expired', { expiry: new Date(expiry), now: new Date(now) });
    }

    return isExpired;
  }

  /**
   * 設定 Token 過期時間
   * @param expiresIn 過期時間（毫秒），預設 24 小時
   */
  static setTokenExpiry(expiresIn: number = SECURITY_CONSTANTS.TOKEN_EXPIRY_MS): void {
    const expiry = Date.now() + expiresIn;
    sessionStorage.setItem('token_expiry', expiry.toString());
    logger.info('Token expiry set', { expiresAt: new Date(expiry) });
  }

  /**
   * 清除 Token 過期時間
   */
  static clearTokenExpiry(): void {
    sessionStorage.removeItem('token_expiry');
    logger.info('Token expiry cleared');
  }

  /**
   * 取得 Token 剩餘時間（毫秒）
   * @returns 剩餘時間（毫秒），若已過期或無 Token 返回 0
   */
  static getTimeUntilExpiry(): number {
    const expiryStr = sessionStorage.getItem('token_expiry');
    if (!expiryStr) return 0;

    const expiry = parseInt(expiryStr, 10);
    if (isNaN(expiry)) return 0;

    const remaining = expiry - Date.now();
    return Math.max(0, remaining);
  }

  /**
   * 檢查 Token 是否即將過期（剩餘時間少於 5 分鐘）
   * @returns true 表示即將過期
   */
  static isTokenExpiringSoon(): boolean {
    const remaining = this.getTimeUntilExpiry();
    const fiveMinutes = 5 * 60 * 1000;
    return remaining > 0 && remaining < fiveMinutes;
  }

  /**
   * 檢查並自動刷新 Token
   * 🚧 TODO: 實作 refresh token API 呼叫
   * @returns Promise<boolean> 成功返回 true，失敗返回 false
   */
  static async checkAndRefreshToken(): Promise<boolean> {
    if (!this.isTokenExpired()) {
      return true;
    }

    logger.warn('Token expired, refresh needed');

    try {
      // 🚧 TODO: 實作 refresh token API 呼叫
      // const newToken = await refreshTokenApi();
      // await setAuthenticated(user, newToken);
      // this.setTokenExpiry();

      logger.error('Token refresh not implemented yet');
      return false;
    } catch (error) {
      logger.error('Token refresh failed:', error);
      return false;
    }
  }

  /**
   * 格式化剩餘時間為可讀字串
   * @returns 格式化的剩餘時間字串
   */
  static formatTimeUntilExpiry(): string {
    const remaining = this.getTimeUntilExpiry();
    if (remaining === 0) return '已過期';

    const minutes = Math.floor(remaining / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days} 天`;
    if (hours > 0) return `${hours} 小時`;
    if (minutes > 0) return `${minutes} 分鐘`;
    return '不到 1 分鐘';
  }
}

// ========================================================================
// 工具函數
// ========================================================================

/**
 * 初始化 Token 監控
 * 設置定期檢查 Token 狀態的定時器
 * @param interval 檢查間隔（毫秒），預設 1 分鐘
 * @returns 清除函數，用於停止監控
 */
export function initTokenMonitoring(interval: number = 60000): () => void {
  logger.info('Token monitoring initialized', { intervalMs: interval });

  const timerId = window.setInterval(() => {
    if (TokenManager.isTokenExpiringSoon()) {
      logger.warn('Token expiring soon', {
        remaining: TokenManager.formatTimeUntilExpiry()
      });
      // 可以在這裡觸發警告通知
    }

    if (TokenManager.isTokenExpired()) {
      logger.error('Token expired during monitoring');
      // 可以在這裡觸發自動登出
    }
  }, interval);

  // 返回清除函數
  return () => {
    clearInterval(timerId);
    logger.info('Token monitoring stopped');
  };
}
